import React, { useMemo, useState } from 'react'
import { useCaddie } from '../../hooks'
import { formatPrizeTypeLabel, INCENTIVE_BASIS_LABEL } from '../../utils/incentiveDisplay'
import styles from './ModalCaddieProfile.module.scss'

const getCaddieTeamLabel = (teamValue) => {
	if (!teamValue) return null
	const teamMap = {
		spoeum: '스포이음',
		voicecaddie: '보이스캐디',
		mansujung: '만수정',
		other: '기타',
		스포이음: '스포이음',
		보이스캐디: '보이스캐디',
		만수정: '만수정',
		기타: '기타',
	}
	return teamMap[teamValue] || teamValue
}

const getCareerLevelLabel = (level) => {
	if (!level) return ''
	const map = {
		NEWBIE: '신입',
		ONE_TO_THREE: '1년 ~ 3년차',
		FOUR_TO_SIX: '4년 ~ 6년차',
		SEVEN_TO_TEN: '7년 ~ 10년차',
		OVER_TEN: '10년 이상',
	}
	return map[level] || level
}

const getTournamentRankLabel = (rank) => {
	const map = {
		FIRST: '우승',
		SECOND: 'TOP2',
		THIRD: 'TOP3',
		FOURTH: 'TOP4',
		FIFTH: 'TOP5',
		SIXTH: 'TOP6',
		SEVENTH: 'TOP7',
		EIGHTH: 'TOP8',
		NINTH: 'TOP9',
		TENTH: 'TOP10',
	}
	return map[rank] || rank || ''
}

export const ModalCaddieProfile = ({ modalVisible, onClose, user, ...props }) => {
	const [activeTab, setActiveTab] = useState('profile')
	const [activeContractTab, setActiveContractTab] = useState('domestic') // 계약 탭 상태 (국내, 해외)

	// 캐디 디테일 정보 조회
	const { data: caddieDetail, isLoading, error } = useCaddie(user?.id, modalVisible)

	const typeLabel =
		caddieDetail?.user_type === 'tourCaddie'
			? '투어 캐디'
			: caddieDetail?.user_type === 'houseCaddie'
				? '하우스'
				: user?.user_type === 'tourCaddie'
					? '투어 캐디'
					: user?.user_type === 'houseCaddie'
						? '하우스'
						: ''

	// 대표 경력 1개
	const representativeCareer = useMemo(() => {
		const list =
			caddieDetail?.user_tour_caddie?.caddie_careers ||
			user?.user_tour_caddie?.caddie_careers ||
			[]
		return Array.isArray(list) ? list.find((c) => c.is_representative === true) || null : null
	}, [caddieDetail?.user_tour_caddie?.caddie_careers, user?.user_tour_caddie?.caddie_careers])

	const preferred = caddieDetail?.user_preferred_contract?.[0]
	const domestic = preferred?.domesticContract
	const international = preferred?.overseasContract

	const formatCurrency = (amount) => {
		if (amount === null || amount === undefined) return '-'
		try {
			return `${Number(amount).toLocaleString('ko-KR')}원`
		} catch (_) {
			return `${amount}`
		}
	}

	// 해외대회지원금은 퍼센트 값으로 저장됨
	const formatExpenseReimbursementPercent = (value) => {
		if (value === null || value === undefined) return '-'
		try {
			const num = Number(value)
			return `${num}%`
		} catch (_) {
			return `${value}`
		}
	}

	const formatYesNo = (val) => (val ? '가능' : '불가능')

	const formatPayroll = (cycle) => {
		switch (cycle) {
			case 'weekly':
				return '주급'
			case 'monthly':
				return '월급'
			case 'perGame':
				return '경기당'
			default:
				return cycle || '-'
		}
	}

	const formatTaxIncluded = (included) => {
		if (included === undefined || included === null) return '-'
		return included ? '세후' : '세전'
	}

	const getWinnerBonus = (incentives) => {
		if (!Array.isArray(incentives)) return null
		const winner = incentives.find((i) => i.startRank === 1 && i.endRank === 1)
		return winner?.percentage ?? null
	}

	const formatIncentiveRange = (startRank, endRank) => {
		if (startRank === 1 && endRank === 1) return '우승'
		const endLabel = endRank === 0 ? 'CUT' : endRank
		return `${startRank} ~ ${endLabel}`
	}

	// 인센티브 정렬: 우승(1~1) → startRank 오름차순 → 같은 구간이면 CUT(endRank 0)은 마지막
	const sortIncentives = (incentives) => {
		if (!Array.isArray(incentives) || incentives.length === 0) return []
		return [...incentives].sort((a, b) => {
			if (a.startRank !== b.startRank) return a.startRank - b.startRank
			const aEnd = a.endRank === 0 ? 9999 : a.endRank
			const bEnd = b.endRank === 0 ? 9999 : b.endRank
			return aEnd - bEnd
		})
	}

	// 국내 계약 Key-Value 목록 (useMemo)
	const domesticKeyValues = useMemo(() => {
		if (!domestic) return []
		const list = [
			{ key: '세금 포함 여부', value: formatTaxIncluded(domestic.isSalaryTaxIncluded) },
			{ key: '임금 지급 주기', value: formatPayroll(domestic.payrollCycles) },
			{ key: '컷 통과', value: formatCurrency(domestic.weeklySalaryCutThrough) },
			{ key: '컷 아웃', value: formatCurrency(domestic.weeklySalaryCutFail) },
			{ key: '제주 대회 참여', value: formatYesNo(domestic.jejuTournamentParticipation) },
			{ key: '제주 지원금', value: formatCurrency(domestic.expenseReimbursementJeju) },
			{ key: INCENTIVE_BASIS_LABEL, value: formatPrizeTypeLabel(domestic?.prizeType) },
			...(domestic?.incentives?.length > 0
				? [
						{
							key: '인센티브 금액',
							incentiveLines: sortIncentives(domestic.incentives).map(
								(i) =>
									`${formatIncentiveRange(i.startRank, i.endRank)} : ${i.percentage}%`,
							),
						},
					]
				: [{ key: '인센티브 금액', value: '-' }]),
			{ key: '스폰서십 모자', value: formatYesNo(domestic.wearSponsoredHat) },
		]
		// const winnerBonus = getWinnerBonus(preferred?.incentives)
		// if (winnerBonus != null) {
		// 	list.push({ key: '우승 보너스', value: `${winnerBonus}%` })
		// }
		return list
	}, [domestic])

	// 해외 계약 Key-Value 목록 (제주 관련 제외, 해외 지원금만)
	const internationalKeyValues = useMemo(() => {
		if (!international) return []
		return [
			{ key: '세금 포함 여부', value: formatTaxIncluded(international.isSalaryTaxIncluded) },
			{ key: '임금 지급 주기', value: formatPayroll(international.payrollCycles) },
			{ key: '컷 통과', value: formatCurrency(international.weeklySalaryCutThrough) },
			{ key: '컷 아웃', value: formatCurrency(international.weeklySalaryCutFail) },
			{ key: INCENTIVE_BASIS_LABEL, value: formatPrizeTypeLabel(international?.prizeType) },
			...(international?.incentives?.length > 0
				? [
						{
							key: '인센티브 금액',
							incentiveLines: sortIncentives(international.incentives).map(
								(i) =>
									`${formatIncentiveRange(i.startRank, i.endRank)} : ${i.percentage}%`,
							),
						},
					]
				: [{ key: '인센티브 금액', value: '-' }]),
			{ key: '스폰서십 모자', value: formatYesNo(international.wearSponsoredHat) },
			{
				key: '해외 지원금',
				value: formatExpenseReimbursementPercent(international.expenseReimbursement),
			},
		]
	}, [international])

	// 언어 능력 행 렌더링 (IIFE 제거)
	const languageRows = useMemo(() => {
		const langs = caddieDetail?.user_tour_caddie?.language_skills
		if (Array.isArray(langs) && langs.length > 0) {
			return langs.map((lang, idx) => (
				<div
					key={`lang-${idx}`}
					style={{
						display: 'flex',
						flexDirection: 'row',
						justifyContent: 'space-between',
						padding: '4px 12px',
						backgroundColor: '#fbfbfb',
						borderBottom: '1px solid #e0e0e0',
					}}>
					<span
						style={{
							fontFamily: 'Pretendard-SemiBold',
							fontSize: '14px',
							color: '#A0A0A0',
						}}>
						언어
					</span>
					<span
						style={{
							fontFamily: 'Pretendard-Medium',
							fontSize: '14px',
							color: '#4B4A48',
						}}>
						{lang}
					</span>
				</div>
			))
		}
		if (typeof langs === 'string' && langs?.trim().length > 0) {
			return (
				<div
					style={{
						display: 'flex',
						flexDirection: 'row',
						justifyContent: 'space-between',
						padding: '4px 12px',
						backgroundColor: '#fbfbfb',
						borderBottom: '1px solid #e0e0e0',
					}}>
					<span
						style={{
							fontFamily: 'Pretendard-SemiBold',
							fontSize: '14px',
							color: '#A0A0A0',
						}}>
						언어
					</span>
					<span
						style={{
							fontFamily: 'Pretendard-Medium',
							fontSize: '14px',
							color: '#4B4A48',
						}}>
						{langs}
					</span>
				</div>
			)
		}
		return (
			<div
				style={{
					padding: '12px',
					backgroundColor: '#fbfbfb',
					borderBottom: '1px solid #e0e0e0',
				}}>
				<span
					style={{
						fontFamily: 'Pretendard-Medium',
						fontSize: '14px',
						color: '#A0A0A0',
					}}>
					언어 정보가 없습니다.
				</span>
			</div>
		)
	}, [caddieDetail?.user_tour_caddie?.language_skills])

	if (!modalVisible) return null

	return (
		<div
			style={{
				position: 'fixed',
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				backgroundColor: 'rgba(0, 0, 0, 0.5)',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				zIndex: 9999,
			}}
			onClick={onClose}>
			<div
				style={{
					backgroundColor: 'white',
					borderRadius: '16px',
					width: '360px',
					maxHeight: '80vh',
					overflow: 'hidden',
					display: 'flex',
					flexDirection: 'column',
					position: 'relative',
				}}
				onClick={(e) => e.stopPropagation()}>
				<div className={styles.modalScrollBody}>
					<div
						className={styles.modalPhotoHeader}
						style={{
							backgroundImage: `url(${caddieDetail?.profile_image || user?.profile_image || '/images/caddie-default-profile.svg'})`,
						}}>
						{(caddieDetail?.user_tour_caddie?.career_level ||
							user?.user_tour_caddie?.career_level) && (
							<span className={styles.modalHeaderCareerBadge}>
								{getCareerLevelLabel(
									caddieDetail?.user_tour_caddie?.career_level ||
										user?.user_tour_caddie?.career_level,
								)}
							</span>
						)}
						{typeLabel && (
							<span className={styles.modalHeaderTypeBadge}>{typeLabel}</span>
						)}
						<div className={styles.modalPhotoOverlay} />
						<div className={styles.modalInfoOverlay}>
							<span className={styles.modalHeaderName}>
								{caddieDetail?.name || user?.name || '-'}
							</span>
							<span className={styles.modalHeaderTeam}>
								{caddieDetail?.user_tour_caddie?.caddie_team_name
									? getCaddieTeamLabel(
											caddieDetail.user_tour_caddie.caddie_team_name,
										) || '무소속'
									: user?.user_tour_caddie?.caddie_team_name
										? getCaddieTeamLabel(
												user.user_tour_caddie.caddie_team_name,
											) || '무소속'
										: '무소속'}
							</span>
							{representativeCareer && (
								<div className={styles.modalHeaderCareerRow}>
									<span className={styles.modalHeaderCareerLabel}>대표경력</span>
									<span className={styles.modalHeaderCareer}>
										{representativeCareer.tournament_name}
										{representativeCareer.tournament_date
											? ` · ${new Date(representativeCareer.tournament_date).getFullYear()}년`
											: ''}
										{representativeCareer.tournament_rank
											? ` · ${getTournamentRankLabel(representativeCareer.tournament_rank)}`
											: ''}
									</span>
								</div>
							)}
						</div>
					</div>

					{/* 탭 메뉴 비활성화
				<div className={styles.tabMenu}>
					<div
						className={`${styles.tabItem} ${activeTab === 'profile' ? styles.active : ''}`}
						onClick={() => setActiveTab('profile')}>
						프로필
					</div>
					<div
						className={`${styles.tabItem} ${activeTab === 'tours' ? styles.active : ''}`}
						onClick={() => setActiveTab('tours')}>
						가능대회 목록
					</div>
				</div>
				*/}

					{/* 탭 컨텐츠 */}
					<div className={styles.tabContent}>
						{activeTab === 'profile' && (
							<div className={styles.profileContent}>
								{/* 프로필 상세 정보 */}
								<div className={styles.profileSection}>
									<h3
										style={{
											padding: '0px 12px',
											fontSize: '16px',
											fontWeight: '600',
											color: '#1b1b1b',
											marginBottom: '12px',
										}}>
										선호 계약 정보
									</h3>
									<div
										style={{
											display: 'flex',
											flexDirection: 'row',
											gap: '4px',
											padding: '0px 12px',
										}}>
										<div
											className={`${styles.tabItem} ${activeContractTab === 'domestic' ? styles.active : ''}`}
											onClick={() => setActiveContractTab('domestic')}>
											국내
										</div>
										<div
											className={`${styles.tabItem} ${activeContractTab === 'international' ? styles.active : ''}`}
											onClick={() => setActiveContractTab('international')}>
											해외
										</div>
									</div>
									<div
										style={{
											display: 'flex',
											flexDirection: 'column',
											marginTop: '12px',
											borderTop: '1px solid #ECECEC',
										}}>
										{activeContractTab === 'domestic' && (
											<>
												{domesticKeyValues.map((row) => (
													<div
														key={row.key}
														className={
															row.incentiveLines
																? styles.keyValueRowBlock
																: ''
														}
														style={{
															display: 'flex',
															flexDirection: row.incentiveLines
																? 'column'
																: 'row',
															justifyContent: row.incentiveLines
																? 'flex-start'
																: 'space-between',
															alignItems: row.incentiveLines
																? 'stretch'
																: undefined,
															padding: '8px 12px',
															backgroundColor: '#fbfbfb',
															borderBottom: '1px solid #e0e0e0',
														}}>
														<span
															style={{
																fontFamily: 'Pretendard-SemiBold',
																fontSize: '14px',
																color: '#A0A0A0',
																flexShrink: 0,
															}}>
															{row.key}
														</span>
														{row.incentiveLines ? (
															<ul className={styles.incentiveList}>
																{row.incentiveLines.map(
																	(line, i) => (
																		<li
																			key={i}
																			className={
																				styles.incentiveListItem
																			}>
																			{line}
																		</li>
																	),
																)}
															</ul>
														) : (
															<span
																style={{
																	fontFamily: 'Pretendard-Medium',
																	fontSize: '14px',
																	color: '#4B4A48',
																}}>
																{row.value}
															</span>
														)}
													</div>
												))}
											</>
										)}

										{activeContractTab === 'international' && (
											<>
												{internationalKeyValues.map((row) => (
													<div
														key={row.key}
														className={
															row.incentiveLines
																? styles.keyValueRowBlock
																: ''
														}
														style={{
															display: 'flex',
															flexDirection: row.incentiveLines
																? 'column'
																: 'row',
															justifyContent: row.incentiveLines
																? 'flex-start'
																: 'space-between',
															alignItems: row.incentiveLines
																? 'stretch'
																: undefined,
															padding: '8px 12px',
															backgroundColor: '#fbfbfb',
															borderBottom: '1px solid #e0e0e0',
														}}>
														<span
															style={{
																fontFamily: 'Pretendard-SemiBold',
																fontSize: '14px',
																color: '#A0A0A0',
																flexShrink: 0,
															}}>
															{row.key}
														</span>
														{row.incentiveLines ? (
															<ul className={styles.incentiveList}>
																{row.incentiveLines.map(
																	(line, i) => (
																		<li
																			key={i}
																			className={
																				styles.incentiveListItem
																			}>
																			{line}
																		</li>
																	),
																)}
															</ul>
														) : (
															<span
																style={{
																	fontFamily: 'Pretendard-Medium',
																	fontSize: '14px',
																	color: '#4B4A48',
																}}>
																{row.value}
															</span>
														)}
													</div>
												))}
											</>
										)}
									</div>
								</div>
								<div style={{ marginTop: '12px' }}>
									<h3
										style={{
											padding: '0px 12px',
											fontSize: '16px',
											fontWeight: '600',
											color: '#1b1b1b',
											marginBottom: '12px',
										}}>
										경력사항
									</h3>
									<div
										style={{
											display: 'flex',
											flexDirection: 'column',
											marginTop: '12px',
											borderTop: '1px solid #ECECEC',
										}}>
										{caddieDetail?.user_tour_caddie?.caddie_careers &&
										caddieDetail?.user_tour_caddie?.caddie_careers?.length >
											0 ? (
											caddieDetail?.user_tour_caddie?.caddie_careers?.map(
												(career) => (
													<div
														key={career.id}
														style={{
															display: 'flex',
															flexDirection: 'row',
															justifyContent: 'space-between',
															padding: '8px 12px',
															backgroundColor: '#fbfbfb',
															borderBottom: '1px solid #e0e0e0',
														}}>
														<div
															style={{
																display: 'flex',
																flexDirection: 'column',
																flex: 1,
															}}>
															<span
																style={{
																	fontFamily:
																		'Pretendard-SemiBold',
																	fontSize: '14px',
																	color: '#1b1b1b',
																	marginBottom: '2px',
																}}>
																{career.tournament_name}
															</span>
															<span
																style={{
																	fontFamily: 'Pretendard-Medium',
																	fontSize: '12px',
																	color: '#A0A0A0',
																}}>
																{career.tournament_date
																	? career.tournament_date.substring(
																			0,
																			7,
																		)
																	: ''}{' '}
																· {career.league}
															</span>
														</div>
														<span
															style={{
																fontFamily: 'Pretendard-Medium',
																fontSize: '14px',
																color: '#4B4A48',
																alignSelf: 'center',
															}}>
															{career.tournament_rank === 'FIRST'
																? '우승'
																: career.tournament_rank ===
																	  'SECOND'
																	? 'TOP2'
																	: career.tournament_rank ===
																		  'THIRD'
																		? 'TOP3'
																		: career.tournament_rank ===
																			  'FOURTH'
																			? 'TOP4'
																			: career.tournament_rank ===
																				  'FIFTH'
																				? 'TOP5'
																				: career.tournament_rank ===
																					  'SIXTH'
																					? 'TOP6'
																					: career.tournament_rank ===
																						  'SEVENTH'
																						? 'TOP7'
																						: career.tournament_rank ===
																							  'EIGHTH'
																							? 'TOP8'
																							: career.tournament_rank ===
																								  'NINTH'
																								? 'TOP9'
																								: career.tournament_rank ===
																									  'TENTH'
																									? 'TOP10'
																									: career.tournament_rank}
														</span>
													</div>
												),
											)
										) : (
											<div
												style={{
													padding: '12px',
													backgroundColor: '#fbfbfb',
													borderBottom: '1px solid #e0e0e0',
												}}>
												<span
													style={{
														fontFamily: 'Pretendard-Medium',
														fontSize: '14px',
														color: '#A0A0A0',
													}}>
													경력 정보가 없습니다.
												</span>
											</div>
										)}
									</div>
								</div>

								{/* 언어 능력 */}
								<div style={{ marginTop: '12px' }}>
									<h3
										style={{
											padding: '0px 12px',
											fontSize: '16px',
											fontWeight: '600',
											color: '#1b1b1b',
											marginBottom: '12px',
										}}>
										외국어 사용능력
									</h3>
									<div
										style={{
											display: 'flex',
											flexDirection: 'column',
											marginTop: '12px',
											borderTop: '1px solid #ECECEC',
										}}>
										{caddieDetail?.user_tour_caddie?.caddie_languages &&
										caddieDetail?.user_tour_caddie?.caddie_languages?.length >
											0 ? (
											caddieDetail?.user_tour_caddie?.caddie_languages?.map(
												(language) => (
													<div
														key={language.id}
														style={{
															display: 'flex',
															flexDirection: 'row',
															justifyContent: 'space-between',
															padding: '8px 12px',
															backgroundColor: '#fbfbfb',
															borderBottom: '1px solid #e0e0e0',
														}}>
														<span
															style={{
																fontFamily: 'Pretendard-SemiBold',
																fontSize: '14px',
																color: '#1b1b1b',
															}}>
															{language.language_name}
														</span>
														<span
															style={{
																fontFamily: 'Pretendard-Medium',
																fontSize: '14px',
																color: '#4B4A48',
															}}>
															{language.proficiency === 'BEGINNER'
																? '기초회화 가능'
																: language.proficiency ===
																	  'INTERMEDIATE'
																	? '원활한 의사소통 가능'
																	: language.proficiency ===
																		  'ADVANCED'
																		? '네이티브'
																		: language.proficiency}
														</span>
													</div>
												),
											)
										) : (
											<div
												style={{
													padding: '12px',
													backgroundColor: '#fbfbfb',
													borderBottom: '1px solid #e0e0e0',
												}}>
												<span
													style={{
														fontFamily: 'Pretendard-Medium',
														fontSize: '14px',
														color: '#A0A0A0',
													}}>
													언어 정보가 없습니다.
												</span>
											</div>
										)}
									</div>
								</div>

								{/* 소개 */}
								<div style={{ marginTop: '12px' }}>
									<h3
										style={{
											padding: '0px 12px',
											fontSize: '16px',
											fontWeight: '600',
											color: '#1b1b1b',
											marginBottom: '12px',
										}}>
										소개
									</h3>
									<div
										style={{
											display: 'flex',
											flexDirection: 'column',
											marginTop: '12px',
											borderTop: '1px solid #ECECEC',
										}}>
										{caddieDetail?.user_tour_caddie?.self_introduction ? (
											<div
												style={{
													padding: '12px',
													backgroundColor: '#fbfbfb',
													borderBottom: '1px solid #e0e0e0',
												}}>
												<span
													style={{
														fontFamily: 'Pretendard-Medium',
														fontSize: '14px',
														color: '#4B4A48',
														lineHeight: '1.5',
														wordBreak: 'break-word',
													}}>
													{
														caddieDetail?.user_tour_caddie
															?.self_introduction
													}
												</span>
											</div>
										) : (
											<div
												style={{
													padding: '12px',
													backgroundColor: '#fbfbfb',
													borderBottom: '1px solid #e0e0e0',
												}}>
												<span
													style={{
														fontFamily: 'Pretendard-Medium',
														fontSize: '14px',
														color: '#A0A0A0',
													}}>
													소개 정보가 없습니다.
												</span>
											</div>
										)}
									</div>
								</div>
							</div>
						)}

						{/* 가능대회 목록 탭 비활성화
					{activeTab === 'tours' && (
						<div className={styles.toursContent}>
							<div className={styles.toursSection}>
								<h3>가능대회 목록</h3>
								<p>여기에 가능대회 목록이 들어갑니다.</p>
							</div>
						</div>
					)}
					*/}
					</div>
				</div>

				{/* 닫기 버튼 */}
				<div
					style={{
						flexShrink: 0,
						width: '100%',
						backgroundColor: '#ffffff',
						borderTop: '1px solid #e0e0e0',
						padding: '12px',
					}}>
					<button
						onClick={onClose}
						style={{
							width: '100%',
							padding: '8px',
							fontFamily: 'Pretendard-SemiBold',
							fontSize: '14px',
							color: '#1b1b1b',
							border: 'none',
							borderRadius: '4px',
							cursor: 'pointer',
						}}>
						닫기
					</button>
				</div>
			</div>
		</div>
	)
}
